import { useCardContainerContext } from '../internal/CardContainerContext';
import { CardHeader, CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { useMemo } from 'react';
import { CARD_FORMATTER_IFRAME_LOOKUP_KEYS } from '../constants/IframeLookupKeys';

// Action components
import ActionErrorTag from '../action-formatters/ActionErrorTag';
import CardActionButtonList from '../action-formatters/CardActionButtonList';
import TruncatedTitle from '../components/TruncatedTitle';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';

// Masonry-specific constants
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ACTIONS_SELECTOR = 'crm-card-actions';

/**
 * MasonryCardFormatter - A card formatter optimized for masonry/grid layouts.
 *
 * Features:
 * - Compact design with drag handles
 * - Optimized spacing for grid layouts
 * - Built-in drag and drop support
 * - Action toolbar support
 */
export default function MasonryCardFormatter({
  actionComponent,
  actionConfigs,
  cardType,
  children,
  hasActionsLoadingError,
  legacyDataAttributes: {
    ['data-sidebar-card-type']: legacyDataSidebarCardType,
    ['data-sidebar-card-association-object-type-id']: legacyDataSidebarCardAssociationObjectTypeId,
    ['data-sidebar-key']: legacyDataSidebarKey
  } = {},
  title,
  ActionFormatter = CardActionButtonList
}) {
  const {
    isSingleCardRenderer
  } = useCardContainerContext();
  const toolbar = useMemo(() => {
    if (hasActionsLoadingError) {
      return /*#__PURE__*/_jsx(ActionErrorTag, {});
    }
    if (actionComponent) {
      return actionComponent;
    }
    if (actionConfigs) {
      return /*#__PURE__*/_jsx(ActionFormatter, {
        actionConfigs: actionConfigs
      });
    }
    return undefined;
  }, [hasActionsLoadingError, actionConfigs, actionComponent, ActionFormatter]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    "data-sidebar-card-type": legacyDataSidebarCardType,
    "data-sidebar-card-association-object-type-id": legacyDataSidebarCardAssociationObjectTypeId,
    "data-sidebar-key": legacyDataSidebarKey,
    "data-card-type": cardType,
    compact: true,
    flush: isSingleCardRenderer,
    marginBlockEnd: isSingleCardRenderer ? '0' : '400',
    children: [(title || toolbar) && /*#__PURE__*/_jsx("div", {
      "data-masonry-card-drag-handle": "true",
      children: /*#__PURE__*/_jsx(DragHandle, {
        size: "medium",
        children: /*#__PURE__*/_jsx(CardHeader, {
          title: /*#__PURE__*/_jsx(TruncatedTitle, {
            title: title
          }),
          titleSize: "small",
          paddingBlockEnd: "300",
          paddingBlockStart: "400",
          toolbar: toolbar && /*#__PURE__*/_jsx("span", {
            "data-selenium-test": ACTIONS_SELECTOR,
            children: toolbar
          })
        })
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      paddingBlockStart: "0",
      children: children
    })]
  });
}
MasonryCardFormatter.iframeLookupKey = CARD_FORMATTER_IFRAME_LOOKUP_KEYS.MASONRY_CARD_FORMATTER;